/*******************************************************************************
 NAME     : engineCapability
 PURPOSE  : FE-01 — Capability inventory client for engine-driven action visibility.

 CONTRACT : GET /project/engine-capabilities — no body
            (FE_INTEGRATION_API.md §3, verified on the local stack 2026-08-16)

   Served by project-management, which relays the Integration Service verbatim.
   FE never calls IS directly — it is internal-only with no inbound auth.

   {
     "ok": true,
     "result": {
       "langflow": [
         { "operation": "connect_tool", "area": "tool",
           "capability_mode": "native", "dispatches": true },
         ...
       ]
     },
     "error": null
   }

 Three things this shape decides, all of them load-bearing:

   1. Operation ids are snake_case. See canonicalOperation — both sides of every
      lookup are normalized, so a camelCase constant still matches.
   2. `dispatches` carries the refusal, and an operation ABSENT from a list we
      hold is refused too: "Op không có trong danh sách = engine từ chối — ví dụ
      rename/list workspace cố ý không có mặt". That is what `declared` tracks.
   3. The result is keyed by provider and carries every provider IS knows, so a
      caller has to say which engine it wants. Only seeds/langflow.json ships
      today, so a Dify project finds no key, reads as undeclared, and keeps every
      button — the as-is path is protected by that, not by an exception for it.

 This module holds NO React and NO framework imports on purpose: the whole
 decision surface is pure functions over a plain inventory object, so it is unit
 testable without a DOM (see common/client-logic/tests/engineCapability.test.js).
 useEngineCapability.js is only a thin React binding over these functions.

 REVISIONS:
 Ver.       Date        Author           Description
 ---------  ----------  ---------------  ------------------------------------
 1.0        2026-08-13  HieuNT161        1. FE-01 최초 작성
 *******************************************************************************/

/** Endpoint of the RS-02 capability relay, on project-management. */
export const CAPABILITY_ENDPOINT = "/project/engine-capabilities";

/**
 * How the FE actually reaches that endpoint.
 *
 * NOT by requesting CAPABILITY_ENDPOINT: the Java backend exposes no REST route
 * of that path — every screen call goes through the E3 transaction relay
 * (POST /tran with serviceId + process), and `Srv110Service.selectEngineCapabilities`
 * is the method that forwards to project-management. Calling the path directly
 * answers 403/404, which degrades the inventory, which silently allows every
 * refused operation. Hence the process name lives next to the endpoint it relays.
 */
export const CAPABILITY_SERVICE_ID = "Srv110Service";
export const CAPABILITY_PROCESS = "selectEngineCapabilities";

/**
 * What the guard decided for one operation.
 * Mirrors `engine_resource_binding.capability_mode` / the IS-02 guard outcome.
 */
export const CAPABILITY_MODE = {
  /** Dispatch normally. */
  EXECUTE: "execute",
  /** Refuse with a reason code (HTTP 501 from the Integration Service). */
  REFUSE: "refuse",
  /** A different path runs and the UI must say so. */
  SUBSTITUTE: "substitute",
};

/**
 * Neutral refusal reason codes (design rev 5 §error catalog).
 * FE-01 only carries the code; FE-04 maps it to display text.
 */
export const REFUSAL_REASON = {
  /** classification unsupported-fail-closed — the provider has no equivalent. */
  RUNTIME_UNSUPPORTED: "RUNTIME_UNSUPPORTED",
  /** classification defer-live — deferred, evidence carried in `reason`. */
  RUNTIME_DEFER_LIVE: "RUNTIME_DEFER_LIVE",
  /** classification MAXIS-side-addition — the product side owns this, not the provider. */
  RUNTIME_NOT_BACKED: "RUNTIME_NOT_BACKED",
  /** classification blocked-by-target — carries the blocking target version. */
  RUNTIME_BLOCKED: "RUNTIME_BLOCKED",
  /** Capability or provider declared but switched off (`ai_engine.is_enabled`). */
  RUNTIME_DISABLED: "RUNTIME_DISABLED",
  /** Nothing declared for this operation yet — the IS-02 default. */
  RUNTIME_UNDECLARED: "RUNTIME_UNDECLARED",
};

/**
 * Canonical operation ids of the neutral contract (design rev 5 §3.7).
 * Grouped in provisioning order, same as the 24-feature table.
 */
export const ENGINE_OPERATION = {
  // Workspace (features 2, 4, 5, 6)
  PROVISION_WORKSPACE: "provisionWorkspace",
  LIST_WORKSPACES: "listWorkspaces",
  RENAME_WORKSPACE: "renameWorkspace",
  DELETE_WORKSPACE: "deleteWorkspace",
  // Workspace members (features 3, 7, 8, 9)
  INSTALL_WORKSPACE_PLUGINS: "installWorkspacePlugins",
  INVITE_WORKSPACE_MEMBERS: "inviteWorkspaceMembers",
  GET_MEMBER_STATE: "getMemberState",
  REMOVE_WORKSPACE_MEMBER: "removeWorkspaceMember",
  // Access (features 10, 11)
  GET_RUNTIME_LAUNCH: "getRuntimeLaunch",
  RESET_RUNTIME_LOGIN: "resetRuntimeLogin",
  // Model (feature 12)
  SYNC_MODEL: "syncModel",
  // Tool (features 13-16)
  CONNECT_TOOL: "connectTool",
  GET_TOOL_INVENTORY: "getToolInventory",
  UPDATE_TOOL: "updateTool",
  DISCONNECT_TOOL: "disconnectTool",
  // Knowledge (feature 17)
  DESCRIBE_KNOWLEDGE_EXPOSURE: "describeKnowledgeExposure",
  // Workflow (features 18-20)
  CREATE_WORKFLOW_APP: "createWorkflowApp",
  LIST_WORKFLOW_APPS: "listWorkflowApps",
  PUBLISH_WORKFLOW_APP: "publishWorkflowApp",
  // Chat (features 21-24)
  RUN_CHAT: "runChat",
  STREAM_CHAT: "streamChat",
  LIST_CHATS: "listChats",
  LIST_MESSAGES: "listMessages",
};

/**
 * `capability_mode` as the design defines it — the PERSISTED label on
 * engine_resource_binding and integration_audit_log: "native | emulated |
 * substitute — how the resource is backed ... mapped from the classification
 * (supported-live → native, adapter-emulatable → emulated, substitute →
 * substitute). ... only executing classifications appear — a refusal creates no
 * row."
 *
 * It is NOT the same axis as CAPABILITY_MODE above, which is the guard's
 * execute / refuse / substitute decision. A payload carrying `capability_mode`
 * is therefore always an executing one, and maps as below.
 */
export const PERSISTED_CAPABILITY_MODE = {
  native: CAPABILITY_MODE.EXECUTE,
  emulated: CAPABILITY_MODE.EXECUTE,
  substitute: CAPABILITY_MODE.SUBSTITUTE,
};

/**
 * The classification vocabulary, from design rev 5's error-code table:
 *   RUNTIME_UNSUPPORTED  ← unsupported-fail-closed
 *   RUNTIME_DEFER_LIVE   ← defer-live, evidence in reason
 *   RUNTIME_NOT_BACKED   ← MAXIS-side-addition
 *   RUNTIME_BLOCKED      ← blocked-by-target, carries the blocking version
 *   RUNTIME_DISABLED     ← capability or provider declared but disabled
 * plus the executing classifications supported-live / adapter-emulatable /
 * substitute from §3.3.
 *
 * The design's §3.5 response envelope carries `capability { classification,
 * enabled, reason }`, so this is the shape the guard actually speaks.
 */
export const CLASSIFICATION_MODE = {
  "supported-live": { mode: CAPABILITY_MODE.EXECUTE, reasonCode: null },
  "adapter-emulatable": { mode: CAPABILITY_MODE.EXECUTE, reasonCode: null },
  substitute: { mode: CAPABILITY_MODE.SUBSTITUTE, reasonCode: null },
  "unsupported-fail-closed": {
    mode: CAPABILITY_MODE.REFUSE,
    reasonCode: REFUSAL_REASON.RUNTIME_UNSUPPORTED,
  },
  "defer-live": {
    mode: CAPABILITY_MODE.REFUSE,
    reasonCode: REFUSAL_REASON.RUNTIME_DEFER_LIVE,
  },
  "MAXIS-side-addition": {
    mode: CAPABILITY_MODE.REFUSE,
    reasonCode: REFUSAL_REASON.RUNTIME_NOT_BACKED,
  },
  "blocked-by-target": {
    mode: CAPABILITY_MODE.REFUSE,
    reasonCode: REFUSAL_REASON.RUNTIME_BLOCKED,
  },
};

/**
 * Fallback used when the inventory cannot be read (relay not deployed yet,
 * network error, malformed payload).
 *
 * `degraded: true` + an empty operation map means every `can()` answers true,
 * which deliberately keeps the **as-is Dify path fully visible**. Per the
 * workspace first-wave rule, existing Dify behavior must keep working before
 * Langflow expands; hiding buttons on a failed lookup would regress it.
 * Screens that must not guess can branch on `inventory.degraded`.
 */
export const FALLBACK_ENGINE_CODE = "dify";

/**
 * The default fallback, allocated once and frozen.
 *
 * A factory call per render would give the object a new identity every time,
 * which kills useMemo in useEngineCapability and turns any consumer that puts
 * the hook result in a dependency array into an infinite loop. Callers must not
 * mutate it — hence Object.freeze.
 */
export const FALLBACK_INVENTORY = Object.freeze({
  engineCode: FALLBACK_ENGINE_CODE,
  operations: Object.freeze({}),
  degraded: true,
  // Nothing was read, so absence of an operation says nothing — see canExecute.
  declared: false,
});

/**
 * @param {string} [engineCode]
 * @returns {Object} the shared frozen fallback for the default engine, or a
 *   fresh one when a specific engine code is known.
 */
export const createFallbackInventory = (engineCode = FALLBACK_ENGINE_CODE) => {
  if (engineCode === FALLBACK_ENGINE_CODE) return FALLBACK_INVENTORY;
  return { engineCode, operations: {}, degraded: true, declared: false };
};

const asPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

/**
 * Canonical form of an operation id: snake_case.
 *
 * The wire carries snake_case. Both integration-service's `seeds/langflow.json`
 * (`get_runtime_launch`, `list_workflow_apps`, `run_chat`, ...) and the verified
 * FE contract (FE_INTEGRATION_API.md §3, `connect_tool`) use it. Design rev 5
 * §3.7 lists the same operations in camelCase.
 *
 * Both sides of every lookup go through here, so a camelCase constant matches a
 * snake_case payload and vice versa. Without it every lookup missed, and since
 * an unmatched operation reads as "nobody restricted it", FE-01 would have
 * quietly reported every action as allowed — the exact failure it exists to
 * prevent.
 *
 * @param {string} operation
 * @returns {string} snake_case
 */
export const canonicalOperation = (operation) => {
  if (typeof operation !== "string" || operation === "") return "";
  return operation
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/-/g, "_")
    .toLowerCase();
};

/**
 * Normalize one operation entry. Accepts the snake_case wire shape and the
 * camelCase shape defensively, plus the bare-string shorthand `"execute"`.
 *
 * @param {Object|string} entry
 * @returns {{mode: string, reasonCode: (string|null), label: (string|null), reason: (string|null)}|null}
 */
export const normalizeOperation = (entry) => {
  if (typeof entry === "string") {
    // Accept a bare classification too, not just a bare mode — runtime-connectors
    // speaks classifications.
    const mapped = CLASSIFICATION_MODE[entry];
    if (mapped) {
      return { mode: mapped.mode, reasonCode: mapped.reasonCode, label: null, reason: null };
    }
    return { mode: entry, reasonCode: null, label: null, reason: null };
  }

  const source = asPlainObject(entry);
  if (!source) return null;

  let mode = source.mode;
  let reasonCode = source.reason_code ?? source.reasonCode ?? null;

  // Design §3.5: the envelope carries `capability { classification, enabled,
  // reason }`. Translate that into the guard's decision rather than reject it.
  const classification = source.classification;
  if ((typeof mode !== "string" || mode.length === 0) && typeof classification === "string") {
    const mapped = CLASSIFICATION_MODE[classification];
    if (mapped) {
      mode = mapped.mode;
      reasonCode = reasonCode ?? mapped.reasonCode;
    }
  }

  // `capability_mode` is the design's PERSISTED label (native | emulated |
  // substitute), a different axis — never a guard decision. Map it, do not
  // copy it: taking "native" as a mode verbatim would make it neither execute
  // nor refuse.
  if (typeof mode !== "string" || mode.length === 0) {
    const persisted = source.capability_mode ?? source.capabilityMode;
    if (typeof persisted === "string") {
      // A value outside native|emulated|substitute is not a capability_mode at
      // all. Older payloads on this branch put a guard decision here, so accept
      // that too rather than silently dropping the entry.
      mode =
        PERSISTED_CAPABILITY_MODE[persisted] ??
        (Object.values(CAPABILITY_MODE).includes(persisted) ? persisted : mode);
    }
  }

  // `dispatches: false` is the shipped refusal signal (integration-service
  // CapabilityDeclaration). The guard publishes every ACTIVE catalog entry and
  // marks the ones nothing can actually run — no active entry plus a handler or
  // a recipe. Its refusal code is RUNTIME_UNDECLARED, and the service is
  // explicit that it is the only one with a producer: "'Never supported', 'not
  // built yet' and 'MAXIS must build it' are no longer distinguished".
  if (source.dispatches === false) {
    mode = CAPABILITY_MODE.REFUSE;
    reasonCode = reasonCode ?? REFUSAL_REASON.RUNTIME_UNDECLARED;
  }

  // `enabled: false` — RUNTIME_DISABLED. No producer today: the administrative
  // switch is deferred and integration-service removed the field with IS-02.
  // Kept because the code gets a producer when that switch lands, and reading a
  // field nobody sends costs nothing.
  if (source.enabled === false && mode !== CAPABILITY_MODE.REFUSE) {
    mode = CAPABILITY_MODE.REFUSE;
    reasonCode = reasonCode ?? REFUSAL_REASON.RUNTIME_DISABLED;
  }

  if (typeof mode !== "string" || mode.length === 0) return null;

  // A substituted operation with no label still has to be announced. The
  // shipped CapabilityDeclaration carries no label field at all — only
  // operation / area / capability_mode / dispatches — so "" here is the normal
  // case, and substituteText turns it into SUBSTITUTE_FALLBACK_LABEL rather
  // than dropping the notice.
  const rawLabel =
    source.label ?? source.substitute_label ?? source.substituteLabel ?? null;
  const label =
    rawLabel ?? (mode === CAPABILITY_MODE.SUBSTITUTE ? "" : null);

  return { mode, reasonCode, label, reason: source.reason ?? null };
};

/**
 * Turn a list of CapabilityDeclaration records into the operation map.
 * Shipped shape: [{operation, area, capability_mode, dispatches}, ...].
 */
const operationsFromDeclarations = (list) => {
  const operations = {};
  for (const entry of list) {
    const item = asPlainObject(entry);
    if (!item) continue;
    const key = canonicalOperation(item.operation ?? item.OPERATION);
    if (!key) continue;
    const normalized = normalizeOperation(item);
    if (normalized) operations[key] = normalized;
  }
  return operations;
};

/**
 * Normalize a raw inventory payload into the shape the selectors below expect.
 * Never throws — a malformed payload degrades to the fallback.
 *
 * Four accepted shapes, because the relay may narrow or forward as-is:
 *   1. {engine_code, operations: {op: {...}}}      this module's own map form
 *   2. Envelope {ok, runtime, result}              integration-service §3.5
 *   3. [{operation, area, capability_mode, dispatches}, ...]
 *                                                  GET /capabilities/{provider}
 *   4. {dify: [...], langflow: [...]}              GET /capabilities (all)
 * For shape 4 the engine has to be picked: `engineCode` says which, and a
 * single-provider map needs no hint.
 *
 * @param {Object|Array} raw - the API payload
 * @param {{engineCode?: string}} [options] - which provider to take from a
 *   multi-provider map
 * @returns {{engineCode: string, operations: Object, degraded: boolean}}
 */
export const normalizeInventory = (raw, options = {}) => {
  const wanted = options.engineCode ? String(options.engineCode).toLowerCase() : null;

  // Shape 3 — a bare declaration list. Only meaningful with a known engine.
  if (Array.isArray(raw) && raw.length && asPlainObject(raw[0])?.operation) {
    return {
      engineCode: wanted ?? FALLBACK_ENGINE_CODE,
      operations: operationsFromDeclarations(raw),
      degraded: false,
      declared: true,
    };
  }

  const payload = Array.isArray(raw) ? raw[0] : raw;
  let source = asPlainObject(payload);
  if (!source) return createFallbackInventory();

  // Shape 2 — unwrap the §3.5 envelope. `runtime` on the envelope names the
  // provider for the single-provider route.
  if (typeof source.ok === "boolean" && "result" in source) {
    if (source.ok === false) return createFallbackInventory();
    const envelopeEngine = typeof source.runtime === "string" ? source.runtime : null;
    return normalizeInventory(source.result, {
      engineCode: wanted ?? envelopeEngine ?? undefined,
    });
  }

  // Shape 4 — a provider-keyed map of declaration lists.
  const providerKeys = Object.keys(source).filter((k) => Array.isArray(source[k]));
  if (providerKeys.length && !source.operations && !source.OPERATIONS) {
    // A hint that matches nothing must NOT fall through to "the only key
    // present". `/capabilities` ships langflow alone today, so asking for dify
    // and getting langflow's declarations back would apply one engine's
    // refusals to the other. The single-key shortcut is for the no-hint case
    // only.
    const key = wanted
      ? providerKeys.find((k) => k.toLowerCase() === wanted) ?? null
      : providerKeys.length === 1
        ? providerKeys[0]
        : null;
    if (!key) return createFallbackInventory(wanted ?? FALLBACK_ENGINE_CODE);
    return {
      engineCode: key.toLowerCase(),
      operations: operationsFromDeclarations(source[key]),
      degraded: false,
      declared: true,
    };
  }

  // All four are design names: engine_code (design rev 5 / est.md DB naming),
  // engine_key (design §launch response), runtime (design §3.5 envelope),
  // ENGINE_TYPE (detail.md, the FE-facing field).
  const engineCode =
    source.engine_code ??
    source.engineCode ??
    source.ENGINE_TYPE ??
    source.engine_key ??
    source.runtime ??
    null;

  const rawOperations = asPlainObject(source.operations ?? source.OPERATIONS);
  if (!rawOperations) {
    return { ...createFallbackInventory(engineCode ?? FALLBACK_ENGINE_CODE) };
  }

  const operations = {};
  for (const [operation, entry] of Object.entries(rawOperations)) {
    const key = canonicalOperation(operation);
    if (!key) continue;
    const normalized = normalizeOperation(entry);
    if (normalized) operations[key] = normalized;
  }

  return {
    engineCode: engineCode ?? FALLBACK_ENGINE_CODE,
    operations,
    degraded: false,
    declared: true,
  };
};

/**
 * @returns {Object|null} the normalized entry, or null when undeclared.
 */
export const selectCapability = (inventory, operation) => {
  const operations = asPlainObject(inventory?.operations);
  const key = canonicalOperation(operation);
  if (!operations || !key) return null;
  return operations[key] ?? null;
};

/**
 * Should the action be offered at all?
 *
 * Two different meanings of "not in the list", and they must not be conflated:
 *
 *   - `declared: true` — we hold this engine's real declaration set. An
 *     operation absent from it is REFUSED. FE_INTEGRATION_API.md §3 is explicit:
 *     "Op không có trong danh sách = engine từ chối — ví dụ rename/list
 *     workspace cố ý không có mặt", and integration-service answers every
 *     undeclared operation with RUNTIME_UNDECLARED.
 *   - `declared: false` — we could not read this engine's list (relay missing,
 *     provider absent from the payload, malformed response). Then absence says
 *     nothing and everything stays offered, which is what keeps the as-is Dify
 *     screens intact. `/capabilities` ships only `seeds/langflow.json` today, so
 *     a Dify project takes exactly this branch.
 */
export const canExecute = (inventory, operation) => {
  const entry = selectCapability(inventory, operation);
  if (!entry) return !inventory?.declared;
  return entry.mode !== CAPABILITY_MODE.REFUSE;
};

/**
 * @returns {string|null} the reason code when the operation is refused.
 */
export const getRefusalReason = (inventory, operation) => {
  const entry = selectCapability(inventory, operation);
  if (!entry) {
    // Absent from a set we actually hold — the service's own single code.
    return inventory?.declared ? REFUSAL_REASON.RUNTIME_UNDECLARED : null;
  }
  if (entry.mode !== CAPABILITY_MODE.REFUSE) return null;
  return entry.reasonCode ?? REFUSAL_REASON.RUNTIME_UNDECLARED;
};

/**
 * @returns {boolean} true when a labeled substitute path runs instead.
 */
export const isSubstituted = (inventory, operation) => {
  const entry = selectCapability(inventory, operation);
  return Boolean(entry) && entry.mode === CAPABILITY_MODE.SUBSTITUTE;
};

/**
 * @returns {string|null} the label to show next to a substituted action.
 */
export const getSubstituteLabel = (inventory, operation) => {
  const entry = selectCapability(inventory, operation);
  if (!entry || entry.mode !== CAPABILITY_MODE.SUBSTITUTE) return null;
  return entry.label ?? null;
};

/**
 * Fetch + normalize the inventory.
 *
 * `request` is injected (rather than importing apiFetch here) so this module
 * stays free of `import.meta.env` and remains unit testable in plain Node.
 * useEngineCapability passes `useFetch().request`.
 *
 * @param {Function} request - useFetch().request
 * @param {{projectId?: (string|number)}} [options]
 * @returns {Promise<Object>} a normalized inventory; the fallback on any failure
 */
export const fetchEngineCapabilities = async (request, options = {}) => {
  if (typeof request !== "function") return createFallbackInventory();

  // No parameters: the relay takes no body and the inventory is per-ENGINE, not
  // per-project — it publishes every provider's declarations at once. The
  // caller's engine is what narrows it, and that is `engineCode` below, not a
  // query string. Sending PROJECT_ID here would also have put a project id in a
  // URL, hence in access logs, for nothing.
  const { engineCode } = options;

  try {
    const response = await request(
      null,
      {
        serviceId: CAPABILITY_SERVICE_ID,
        process: [{ [CAPABILITY_PROCESS]: "엔진 지원 정보 조회" }],
      },
      {
        // No dialog: a missing relay must not pop an error on every screen.
        showLoad: false,
        showAlert: false,
        // A distinct reqType is required, not cosmetic. useFetch's error path
        // calls FinishLoading(reqType, "error"), and reqType defaults to
        // "LOADING" — the id other in-flight requests use. Without this, a
        // failed capability read turns an unrelated request's loading toast red
        // and tears down its overlay.
        reqType: "ENGINE_CAPABILITY",
      }
    );
    if (!response) return createFallbackInventory(engineCode);
    // The E3 relay answers {data: {result: <the IS envelope>}}; normalizeInventory
    // takes the envelope. The fallbacks keep a raw payload working, so a future
    // direct route needs no change here.
    const payload = response?.data?.result ?? response?.result ?? response;
    return normalizeInventory(payload, { engineCode });
  } catch (e) {
    return createFallbackInventory(engineCode);
  }
};
